import { useQuery } from '@tanstack/react-query'
import { uniq } from 'lodash'
import { useMemo, useState } from 'react'

import { useDebounced } from '../../hooks/useDebounced'
import { apiClient } from '../../modules/apiclient'
import { ComboBox } from './fields/ComboBox'

type Props = {
    onSelect?: (address: string) => void
    placeholder?: string
    size?: 'small' | 'medium'
    defaultAddress?: string
    shouldFilterAddresses?: boolean
} & (
    | { searchValue: string; setSearchValue: (value: string) => void }
    | { searchValue?: undefined; setSearchValue?: undefined }
)

export const AddressPicker = ({
    size = 'small',
    shouldFilterAddresses = false,
    placeholder,
    onSelect,
    defaultAddress,
    searchValue,
    setSearchValue,
}: Props) => {
    const [_searchValue, _setSearchValue] = useState(defaultAddress ?? '')

    const effectiveSearchValue = searchValue ?? _searchValue
    const effectiveSetSearchValue = setSearchValue ?? _setSearchValue

    const debouncedSearchValue = useDebounced(effectiveSearchValue, 250)

    const { data: locationAutocompleteResult, isLoading } = useQuery({
        queryKey: ['address-picker', debouncedSearchValue],
        queryFn: () =>
            apiClient.post<{ predictions: { description: string }[] }>(
                `/location/autocomplete?q=${encodeURIComponent(debouncedSearchValue)}&filter_address=${shouldFilterAddresses ?? false}`
            ),
        enabled: debouncedSearchValue.length > 0,
    })

    const suggestedAddresses = useMemo(() => {
        if (locationAutocompleteResult === undefined) return []
        return locationAutocompleteResult.data.predictions.map(
            (prediction) => prediction.description
        )
    }, [locationAutocompleteResult])

    const items = useMemo(() => {
        return uniq(suggestedAddresses).map((address) => ({
            id: address,
            title: address,
        }))
    }, [suggestedAddresses])

    const handleSelectItem = (item: { id: string } | null) => {
        if (item === null) {
            effectiveSetSearchValue('')
            onSelect?.('')
            return
        }

        effectiveSetSearchValue(item.id)
        onSelect?.(item.id)
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur()

            setTimeout(() => {
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur()
                }
            })
        }
    }

    return (
        <ComboBox
            size={size}
            placeholder={placeholder}
            searchValue={effectiveSearchValue}
            onSearchValueChange={effectiveSetSearchValue}
            onClearSearchValue={() => effectiveSetSearchValue('')}
            items={items}
            onSelectItem={handleSelectItem}
            hasChevron={false}
            isLoading={effectiveSearchValue.length > 0 && isLoading}
        />
    )
}
